import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { analyticsTrack } from '../../../analyticsUtils';
import { putRequest } from '../../../requestUtils';
import ProjectLoadingIndicator from '../ProjectLoadingIndicator';
import ExpertiseTagDropdown from '../../common/ExpertiseTagDropdown';
import '../ProjectDetails.scss';

const models = [
  'area_of_expertise',
  'therapeutic_area',
  'disease_code',
  'therapy',
  'regulatory_pathway',
  'development_phase',
  'degree',
  'language',
  'skill',
  'accreditation',
];

const formStep = 'qualifications';

const maxItems = 100;

const filterPicklist = (picklist, input) => picklist.filter(
  (record) => record.label.toLowerCase().includes(input.toLowerCase()),
).slice(0, maxItems);

class ProjectExpertise extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showModal: false,
      errors: null,
      expertiseTags: null,
      expertisePicklists: {},
      notApplicable: [],
    };

    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleCreate = this.handleCreate.bind(this);
    this.handleToggleNotApplicable = this.handleToggleNotApplicable.bind(this);
  }

  componentDidMount() {
    const { projectResourceId, getProjectDetails } = this.props;

    if (!projectResourceId) return;

    getProjectDetails(formStep).then(({ data }) => {
      this.setState({
        expertiseTags: data.expertise_tags,
        expertisePicklists: data.expertise_picklists || {},
        notApplicable: data.not_applicable,
      });
    });
  }

  ableToSubmit() {
    const { expertiseTags, notApplicable } = this.state;

    const tagSum = models.reduce((aggr, model) => aggr + expertiseTags[`${model}_ids`].length, 0);

    // to be able to submit, every model should either have tags or be not applicable.
    return models.every(
      (model) => (expertiseTags[`${model}_ids`].length > 0
        || notApplicable.includes(model) || ['degree', 'skill', 'language', 'accreditation'].includes(model)),
    ) && tagSum >= 3;
  }

  handleShowModal() {
    this.setState({ showModal: true });
  }

  handleHideModal() {
    this.setState({ showModal: false });
  }

  handleBack() {
    const { formType, back } = this.props;
    if (formType === 'page') {
      back();
    } else {
      this.handleHideModal();
    }
  }

  handleSubmit() {
    const { csrfToken, projectResourceId, afterSubmit } = this.props;
    const { expertiseTags, notApplicable } = this.state;
    const projectAttributes = { ...expertiseTags };

    models.forEach((model) => {
      if (model === 'therapy') {
        projectAttributes.therapies_not_applicable = notApplicable.includes(model);
      } else {
        projectAttributes[`${model}s_not_applicable`] = notApplicable.includes(model);
      }
    });

    putRequest(
      `/projects/${projectResourceId}`,
      { project: projectAttributes },
      csrfToken,
    ).then(() => {
      afterSubmit();
    }).catch((error) => {
      this.setState({ errors: error.name });
      return null;
    });
  }

  handleSelect(model, multi) {
    const { expertiseTags } = this.state;

    return ((tags) => {
      const allTags = expertiseTags;
      if (multi) {
        allTags[`${model}_ids`] = tags.map((tag) => tag.value);
      } else {
        allTags[`${model}_ids`] = tags.length >= 1 ? [tags[tags.length - 1].value] : [];
      }
      if (tags.length >= 1) {
        analyticsTrack(
          `Selected ${model}: ${tags[tags.length - 1].label}`,
          { location: 'Project creation' },
        );
      } else {
        analyticsTrack(
          `Cleared ${model} tags`,
          { location: 'Project creation' },
        );
      }
      this.setState({ expertiseTags: allTags });
    });
  }

  handleCreate(model, multi) {
    const { expertiseTags } = this.state;

    return ((inputValue) => {
      const allTags = expertiseTags;
      if (multi) {
        allTags[`${model}_ids`].push(`custom:${inputValue}`);
      } else {
        allTags[`${model}_ids`] = [`custom:${inputValue}`];
      }
      this.setState({ expertiseTags: allTags });
    });
  }

  handleToggleNotApplicable(e) {
    const { expertiseTags, notApplicable } = this.state;

    const { model } = e.target.dataset;
    const newExpertiseTags = { ...expertiseTags };

    analyticsTrack(
      `Toggled not applicable on ${model}`,
      { location: 'Project creation' },
    );

    if (notApplicable.includes(model)) {
      const modelIndex = notApplicable.findIndex((elm) => elm === model);
      notApplicable.splice(modelIndex, 1);

      if (model === 'therapeutic_area') {
        const diseaseIndex = notApplicable.findIndex((elm) => elm === 'disease_code');
        if (diseaseIndex !== -1) notApplicable.splice(diseaseIndex, 1);
      }
    } else {
      notApplicable.push(model);
      newExpertiseTags[`${model}_ids`] = [];

      if (model === 'therapeutic_area' && !notApplicable.includes('disease_code')) {
        notApplicable.push('disease_code');
      }
    }

    this.setState({ notApplicable, expertiseTags: newExpertiseTags });
  }

  renderModal() {
    return (
      <Modal
        show={this.state.showModal}
        onHide={this.handleHideModal}
        dialogClassName="add-coworkers-modal"
      >
        <Modal.Header closeButton />
        <Modal.Body className="add-coworkers-modal-body">
          {this.renderContent()}
        </Modal.Body>
      </Modal>
    );
  }

  renderButtons() {
    const { backButtonText, submitButtonText } = this.props;

    return (
      <div className="footer-buttons-container mt-4">
        <button className="btn btn-secondary button-back" onClick={this.handleBack}>
          {backButtonText}
        </button>
        <button
          className="btn btn-primary button-submit"
          onClick={this.handleSubmit}
          disabled={!this.ableToSubmit()}
        >
          {submitButtonText}
        </button>
      </div>
    );
  }

  renderErrors() {
    const { errors } = this.state;

    return (errors
      && (
      <div id="error_explanation">
        <h3>Errors prohibited this project from being saved:</h3>
        <ul>
          {errors.map((e) => (
            <li>{e}</li>
          ))}
        </ul>
      </div>
      )
    );
  }

  renderMessages() {
    const { expertiseTags } = this.state;

    const tagSum = models.reduce((aggr, model) => aggr + expertiseTags[`${model}_ids`].length, 0);

    return (
      <div id="error_explanation">
        <ul>
          { expertiseTags.area_of_expertise_ids.length === 0
            ? <li>Functional Area must be selected.</li> : '' }
          { expertiseTags.area_of_expertise_ids.length && tagSum >= 1 && tagSum < 3
            ? <li>At least 3 tags must be applied describing the project.</li> : '' }
          { expertiseTags.area_of_expertise_ids.length && tagSum >= 3 && !this.ableToSubmit()
            ? <li>Each section must have at least one tag or marked &apos;Not Applicable&apos;.</li>
            : ''}
        </ul>
      </div>
    );
  }

  renderNotApplicableCheckbox(model) {
    const { notApplicable } = this.state;

    return (
      <div className="not-applicable-checkbox">
        <label className="custom-checkbox-container" htmlFor={`notApplicable-${model}`}>
          <input
            type="checkbox"
            data-model={model}
            onClick={this.handleToggleNotApplicable}
            id={`notApplicable-${model}`}
            checked={notApplicable.includes(model)}
          />
          <span className="checkmark" />
          <span className="checkbox-text">Not applicable</span>
        </label>
      </div>
    );
  }

  renderSelect(model, heading, asyncUrl, isCreate, multi, notApplicableCheckbox) {
    const { expertiseTags, notApplicable, expertisePicklists } = this.state;
    const picklist = expertisePicklists[model] || [];
    const selection = expertiseTags[`${model}_ids`].map((id) => {
      const foundOption = picklist.find((option) => (option.value === id));
      return foundOption || (typeof (id) === 'string' ? { label: id.slice(7), value: id } : undefined);
    }).filter(Boolean);
    const disabled = notApplicable.includes(model);

    return (
      <div className={`${model}`}>
        <ExpertiseTagDropdown
          model={model}
          selection={selection}
          handleSelect={this.handleSelect}
          handleCreate={this.handleCreate}
          multi={multi}
          isValidNewOption={() => isCreate}
          isCreatable={isCreate}
          heading={heading}
          isDisabled={disabled}
          picklist={picklist}
          loadOptions={(input) => (
            new Promise((resolve) => { resolve(filterPicklist(picklist, input)); })
          )}
          groupStyle={model === 'degree'}
        />
        {notApplicableCheckbox && this.renderNotApplicableCheckbox(model)}
      </div>
    );
  }

  renderContent() {
    if (!this.state.expertiseTags) {
      return (
        <ProjectLoadingIndicator />
      );
    }

    return (
      <div className="style-guide project-expertise-container">
        <div className="g-row">
          <div className="g-column g-flex-1" />
          <div className="g-column g-flex-6 expertise-entry-container">
            {this.renderErrors()}
            <h2>Qualifications</h2>
            <div className="heading-subtext">
              Select qualifications and skills required for your project
            </div>
            <br />
            {this.renderSelect('area_of_expertise', 'Functional Areas', '', false, false, false)}
            {this.renderSelect('therapeutic_area', 'Therapeutic Areas', '', false, true, true)}
            {this.renderSelect(
              'disease_code',
              'Diseases and indications',
              '/users/autocomplete_disease_code',
              false,
              true,
              true,
            )}
            {this.renderSelect('therapy', 'Product Technologies', '', false, true, true)}
            {this.renderSelect('regulatory_pathway', 'Regulatory Pathways', '', false, true, true)}
            {this.renderSelect('development_phase', 'Development Phases', '', false, true, true)}
            {this.renderSelect('degree', 'Highest Education', '', false, true, false)}
            {this.renderSelect('language', 'Language', '', false, true, false)}
            {this.renderSelect('accreditation', 'Certification', '', false, true, false)}
            {this.renderSelect('skill', 'Other Keywords / Skills', '', true, true, false)}
            {this.renderMessages()}
            {this.renderButtons()}
          </div>
        </div>
      </div>
    );
  }

  renderModalLink() {
    return (
      <i
        role="button"
        tabIndex={0}
        className="fa fa-edit modal-link"
        onClick={this.handleShowModal}
      >
        {(this.props.formType === 'modal-link') ? 'Edit' : ''}
      </i>
    );
  }

  render() {
    if (this.props.formType === 'page') {
      return this.renderContent();
    }
    return (
      <div>
        { this.renderModalLink() }
        { this.renderModal() }
      </div>
    );
  }
}

ProjectExpertise.propTypes = {
  formType: PropTypes.string, // one of 'page', 'modal-link', 'modal-icon'
  projectResourceId: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  back: PropTypes.func,
  afterSubmit: PropTypes.func,
  getProjectDetails: PropTypes.func.isRequired,
  backButtonText: PropTypes.string,
  submitButtonText: PropTypes.string,
};

ProjectExpertise.defaultProps = {
  formType: 'page',
  projectResourceId: null,
  back: () => {},
  afterSubmit: () => {},
  backButtonText: 'Back',
  submitButtonText: 'Next',
};

export default ProjectExpertise;
